import React, { useCallback, useEffect, useMemo, useState } from 'react';
import TemplatesPageHeader from './components/TemplatesPageHeader';
import TemplatesTable from './components/TemplatesTable';
import { useAdditionalFieldSelection } from '../../hooks/useAdditionalFields';
import TemplateSearchDialog, { describeSearchBlob } from './components/TemplateSearchDialog';
import QuotesActiveFilterBanner from './components/QuotesActiveFilterBanner';
import TemplateCustomFilterDialog from './components/TemplateCustomFilterDialog';
import { summarizeFilter } from './components/CustomFilterDialog';
import { useCreateOrderFlow } from '../pro/createOrder/CreateOrderFlowContext';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { buildTemplateBulkActions } from '../../components/proBulkActions';
import { ViewedProsProvider, useViewedPros } from '../../contexts/ViewedProsContext';
import type { FieldDefinition } from '../../contexts/FieldDefinitionsContext';
import { useCanEditTemplateFields } from '../pro/useCanEditTemplate';
import templateService, { type TemplateRow, type TemplateView, type TemplateStatus } from '../../services/template.service';

/** localStorage key persisting the user's Template|Line-Items view choice. */
const VIEW_MODE_KEY = 'templates.viewMode';

/** Read the persisted view (default 'template'); guards a hostile/empty store. */
function loadView(): TemplateView {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === 'lineitem' ? 'lineitem' : 'template';
  } catch {
    return 'template';
  }
}

/**
 * Cross-navigation cache of the last committed search. TemplatesPage UNMOUNTS
 * when the user opens a template detail (a route change); on return it remounts.
 * Without this, the mount effect would re-run the default search and the user's
 * toggles / search + results would be lost. Held in-memory, so it survives
 * tab-session navigation; a full page reload starts fresh.
 */
interface TemplatesSearchCache {
  view: TemplateView;
  status: TemplateStatus;
  customSearch: string;
  customFilter: string;
  /** The filter's Include Deleted toggle, so re-opening it shows the running scope. */
  filterIncludeDeleted: boolean;
  /** Saved-filter name behind `customFilter`, so the banner comes back titled. */
  filterName: string | null;
  rows: TemplateRow[];
  capped: boolean;
  /** Whether these rows can contain deleted templates. */
  includesDeleted: boolean;
  /** Whether the category choice applied (false for any search/filter). */
  statusApplied: boolean;
  /** Which added columns these rows were fetched WITH. Returning from a detail page
   *  restores the cache; without this the grid would show a column the picker still
   *  lists while the rows behind it never carried its data. */
  additionalFieldsKey: string;
}
let searchCache: TemplatesSearchCache | null = null;
/** Test hook: clear the cross-navigation cache between renders. */
export function __resetTemplatesSearchCache(): void { searchCache = null; }

/**
 * Templates landing page. /order-management/templates.
 *
 * Mirrors the Quotes Accounts view: on mount and on every control change it calls
 * templateService.searchTemplates, which returns the full matching set (uncapped
 * for a category view; a Custom Search / Filter carries a backstop);
 * the grid does sort / column-filter / global-search / pagination CLIENT-side over
 * it. The two segmented views (Template | Line Items), the category picker (Active
 * Approved | Not Approved), the Custom Search box and the Custom Filter builder are
 * what shape the request. Deleted templates are in neither category — a Custom
 * Search or Filter is the only way to see them, and both span every status. View-only — clicking a row opens the
 * existing template detail page.
 */
const TemplatesPageInner: React.FC = () => {
  // Seed from the cross-navigation cache when returning from a detail page.
  const [view, setView] = useState<TemplateView>(() => searchCache?.view ?? loadView());

  // Any orders-scope registry field can be added as a column, resolved against the
  // TEMPLATE tables — the same registry the order grids pick from, a different
  // binding. Keyed per view, like the layout: the two views show different things.
  // `binding` is what drops the fields templates have no counterpart for.
  const fieldSelection = useAdditionalFieldSelection(
    view === 'template' ? 'templates-template-grid' : 'templates-lineitem-grid',
    { binding: 'templates' },
  );
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  // Which of the two categories is on screen. 'approved' is the working set and the
  // default; deleted templates are in neither and come only from a search/filter.
  const [status, setStatus] = useState<TemplateStatus>(() => searchCache?.status ?? 'approved');
  const [customSearch, setCustomSearch] = useState(() => searchCache?.customSearch ?? '');
  // Server's report that a custom search or filter overrode the category for this
  // result (see TemplateSearchResult.includesDeleted). Not derived from "a blob is
  // set": the server only drops the status condition for one that actually parsed.
  const [includesDeleted, setIncludesDeleted] = useState(() => searchCache?.includesDeleted ?? false);
  const [statusApplied, setStatusApplied] = useState(() => searchCache?.statusApplied ?? true);
  // Custom Filter (JSON FilterCondition[]) and Custom Search (one-field multi-value
  // blob) are INDEPENDENT — the backend ANDs them, so either, both, or neither can
  // be active, and each shows its own banner.
  const [customFilter, setCustomFilter] = useState(() => searchCache?.customFilter ?? '');
  // The filter's own Include Deleted choice. A FILTER is a broad query, so it stays on
  // live templates until the user opts in — unlike a custom SEARCH, which names
  // records and always spans every status.
  const [filterIncludeDeleted, setFilterIncludeDeleted] = useState(() => searchCache?.filterIncludeDeleted ?? false);
  // Saved-filter name when the active filter came from one (drives the banner label).
  const [filterName, setFilterName] = useState<string | null>(() => searchCache?.filterName ?? null);
  const [rows, setRows] = useState<TemplateRow[]>(() => searchCache?.rows ?? []);
  const [capped, setCapped] = useState(() => searchCache?.capped ?? false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  // Filter field registry — the PAGE needs it only to turn the active filter's raw
  // fieldnames into display names for the banner chips (the dialog fetches its own
  // copy). Session-cached in the service, so this is at most one HTTP call; a
  // failure is non-fatal, summarizeFilter just falls back to the raw fieldname.
  const [filterFields, setFilterFields] = useState<FieldDefinition[]>([]);
  // Creating a template writes records — same gate as editing template fields, and the
  // same one Change All needs: /pro puts the whole editable template surface behind
  // this single component, so a bulk edit must not need less.
  const canCreate = useCanEditTemplateFields();
  const canEditTemplates = canCreate;
  const { markViewed } = useViewedPros();
  // The template opened in the Pro detail drawer (in place — no navigation).
  // ProDetailSlideOver accepts the raw "T"-prefixed pronumber and fetches itself.
  const [selectedPro, setSelectedPro] = useState<string | null>(null);
  // The Change All target set (every template visible in the grid when the button was
  // pressed). Non-null puts the drawer in change-all mode, anchored on the first.
  const [changeAllTemplates, setChangeAllTemplates] = useState<string[] | null>(null);
  // The template this page just created, until its drawer visit ends. Drives the
  // refetch-on-close below.
  // New Template runs through the SHARED create flow (#33471) — same modal, drawer
  // and staging as a new order, and nothing is persisted until the user saves.
  const { openCreateTemplate } = useCreateOrderFlow();

  const runSearch = useCallback(async (overrides: Partial<{
    view: TemplateView; status: TemplateStatus; customSearch: string;
    customFilter: string; filterName: string | null; filterIncludeDeleted: boolean;
  }> = {}) => {
    const v = overrides.view ?? view;
    const st = overrides.status ?? status;
    const cs = overrides.customSearch !== undefined ? overrides.customSearch : customSearch;
    const cf = overrides.customFilter !== undefined ? overrides.customFilter : customFilter;
    const fid = overrides.filterIncludeDeleted ?? filterIncludeDeleted;
    // Not server-affecting, but it rides along so the cache write below can't
    // record a stale name against a freshly-applied filter. Callers clear it with
    // `null`, not `undefined` — `undefined` is the "not overridden" sentinel, so a
    // name can't be cleared with it (a cleared filter would then resurrect its old
    // `Filter: <name>` title out of the cache).
    const fname = overrides.filterName !== undefined ? overrides.filterName : filterName;
    setLoading(true);
    setError(null);
    try {
      const resp = await templateService.searchTemplates({
        view: v,
        status: st,
        customSearch: cs || undefined,
        customFilter: cf || undefined,
        includeDeleted: fid || undefined,
        additionalFields: fieldSelection.additionalFields,
      });
      searchCache = { view: v, status: st, customSearch: cs, customFilter: cf, filterIncludeDeleted: fid, filterName: fname, additionalFieldsKey, rows: resp.rows, capped: resp.capped, includesDeleted: resp.includesDeleted, statusApplied: resp.statusApplied };
      setRows(resp.rows);
      setCapped(resp.capped);
      setIncludesDeleted(resp.includesDeleted);
      setStatusApplied(resp.statusApplied);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      setRows([]);
      setCapped(false);
      setIncludesDeleted(false);
      setStatusApplied(true);
    } finally {
      setLoading(false);
    }
  }, [view, status, customSearch, customFilter, filterIncludeDeleted, filterName, additionalFieldsKey]);

  // Initial load on mount — once, with defaults. Returning from a detail page
  // (searchCache set) skips the re-fetch; the initializers already restored state.
  useEffect(() => {
    // The saved selection is part of the request; going early just fetches twice.
    if (!fieldSelection.ready) return;
    // A cache from a different column selection is stale in a way the rows cannot
    // show, so it re-fetches rather than being reused.
    if (searchCache && searchCache.additionalFieldsKey === additionalFieldsKey) return;
    void runSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldSelection.ready, additionalFieldsKey]);

  // Field display names for the active-filter chips. Runs on EVERY mount (unlike
  // the search above) because it isn't part of the search cache — the service's
  // session cache makes the repeat a no-op. Silent on failure: raw fieldnames.
  useEffect(() => {
    templateService.getFilterFields().then(setFilterFields).catch(() => { /* raw fieldnames */ });
  }, []);

  const handleViewChange = useCallback((next: TemplateView) => {
    if (next === view) return;
    setView(next);
    try { localStorage.setItem(VIEW_MODE_KEY, next); } catch { /* non-fatal */ }
    void runSearch({ view: next });
  }, [view, runSearch]);

  const handleStatusChange = useCallback((next: TemplateStatus) => {
    if (next === status) return;
    setStatus(next);
    void runSearch({ status: next });
  }, [status, runSearch]);

  const applyCustomSearch = (blob: string) => {
    setCustomSearch(blob);
    setSearchOpen(false);
    void runSearch({ customSearch: blob });
  };

  const clearCustomSearch = () => {
    setCustomSearch('');
    void runSearch({ customSearch: '' });
  };

  /** `name` is set only when the applied filter came from a saved one, unmodified. */
  const applyCustomFilter = (filter: string, name?: string, includeDeleted?: boolean) => {
    const incl = !!includeDeleted;
    setCustomFilter(filter);
    setFilterName(name ?? null);
    setFilterIncludeDeleted(incl);
    setFilterOpen(false);
    void runSearch({ customFilter: filter, filterName: name ?? null, filterIncludeDeleted: incl });
  };

  const clearCustomFilter = () => {
    setCustomFilter('');
    setFilterName(null);
    // The scope goes with the filter: there is nothing left for it to widen, and a
    // sticky value would silently apply to the next filter the user builds.
    setFilterIncludeDeleted(false);
    void runSearch({ customFilter: '', filterName: null, filterIncludeDeleted: false });
  };

  // Fires when the SHARED create flow commits a template (#33471). Deliberately
  // does NOT touch `selectedPro`: that opens THIS page's drawer, and the flow is
  // already showing its own on the new template — setting it would stack a second
  // drawer on the same record.
  const handleCreated = useCallback((pronumber: string) => {
    markViewed(pronumber);
    // Forces a fresh fetch if the page remounts before the close refetch runs.
    searchCache = null;
    // The grid has to be told: a new template is unapproved, so it belongs in the
    // current results right away when the Not Approved category is on screen
    // (otherwise the create looks like it did nothing). In the Approved category it
    // is out of scope — the refetch that matters is the flow's onClosed.
    if (status === 'unapproved') void runSearch();
  }, [markViewed, status, runSearch]);

  /**
   * Closing THIS page's drawer (an ordinary row click) refetches nothing — nothing
   * has necessarily changed.
   *
   * The create case that used to live here — "the user may have approved it in the
   * drawer, which moves the row between categories, so always refetch" — moved to
   * the shared flow's `onClosed` callback (#33471), because the create no longer
   * opens this page's drawer at all: the flow owns its own.
   */
  const handleDrawerClose = useCallback(() => {
    setSelectedPro(null);
  }, []);

  const openTemplate = useCallback((row: TemplateRow) => {
    const pro = String(row.pronumber ?? '');
    if (pro) setSelectedPro(pro);
  }, []);

  /**
   * Change All — the templates in this table, fanned out from one anchor.
   *
   * Same shape as Search Results' PRO version: the drawer opens on the FIRST target as
   * the anchor, in change-all mode, and the fields flagged there apply to the rest on
   * Save. Only the checkboxes the template policy currently leaves unlocked appear, and
   * each target is re-judged on its own approval server-side, so a set mixing approved
   * and unapproved templates is reported record by record in the review modal rather
   * than part-applied.
   *
   * The single-template drawer and change-all mode are mutually exclusive — one drawer,
   * two meanings.
   */
  const openChangeAll = useCallback((templates: string[]) => {
    if (templates.length === 0) return;
    setSelectedPro(null);
    setChangeAllTemplates(templates);
  }, []);

  const closeChangeAll = useCallback(() => {
    setChangeAllTemplates(null);
    // The fan-out may have changed rows this grid is showing — refetch so it reflects
    // them rather than the pre-change values.
    void runSearch();
  }, [runSearch]);

  const bulkActions = useMemo(
    () => buildTemplateBulkActions({ canEdit: canEditTemplates, onChangeAll: openChangeAll }),
    [canEditTemplates, openChangeAll],
  );

  // Independent banners — both can show (filter AND search active at once).
  const filterBanner = customFilter ? {
    kind: 'filter' as const,
    title: filterName ? `Filter: ${filterName}` : 'Custom filter',
    // A filter always spans both approval states; whether it also spans DELETED is
    // the user's Include Deleted choice, so say which one is running.
    chips: [
      ...summarizeFilter(customFilter, filterFields),
      filterIncludeDeleted ? 'Incl. deleted' : 'Live templates only',
    ],
    onEdit: () => setFilterOpen(true),
  } : null;

  const searchBanner = customSearch ? (() => {
    const d = describeSearchBlob(customSearch);
    return {
      kind: 'search' as const,
      title: 'Custom search',
      // The extra chip is the user-facing half of includesDeleted: a search returns
      // a named template whatever its status, so deleted and unapproved rows appear
      // here that neither category view would show.
      chips: [
        ...(d ? [`${d.label}: ${d.values}`] : []),
        ...(includesDeleted ? ['All statuses, incl. deleted'] : []),
      ],
      onEdit: () => setSearchOpen(true),
    };
  })() : null;

  return (
    <div className="flex flex-col min-h-screen bg-[#f5f6f8] dark:bg-gray-900">
      <main className="flex-1 min-w-0 flex flex-col">
        <TemplatesPageHeader
          onCustomSearch={() => setSearchOpen(true)}
          customSearchActive={!!customSearch}
          onCustomFilter={() => setFilterOpen(true)}
          customFilterActive={!!customFilter}
          onCreateTemplate={() => openCreateTemplate({
            onCreated: handleCreated,
            // The create flow owns its own drawer, so this page never sees that
            // drawer close — and the template may have been approved in there,
            // which moves it between categories. Refetch unconditionally.
            onClosed: () => { searchCache = null; void runSearch(); },
          })}
          canCreate={canCreate}
        />

        <div className="flex flex-col gap-3.5 px-[22px] pt-3.5 pb-8">
          {filterBanner && <QuotesActiveFilterBanner {...filterBanner} onClear={clearCustomFilter} />}
          {searchBanner && <QuotesActiveFilterBanner {...searchBanner} onClear={clearCustomSearch} />}
          <TemplatesTable
        fieldSelection={fieldSelection}
            rows={rows}
            capped={capped}
            loading={loading}
            error={error}
            view={view}
            onViewChange={handleViewChange}
            status={status}
            onStatusChange={handleStatusChange}
            includesDeleted={includesDeleted}
            statusApplied={statusApplied}
            onOpenTemplate={openTemplate}
            bulkActions={bulkActions}
            canEdit={canEditTemplates}
            onEditsApplied={() => { void runSearch(); }}
          />
        </div>
      </main>

      <TemplateSearchDialog
        open={searchOpen}
        initialBlob={customSearch}
        onClose={() => setSearchOpen(false)}
        onApply={applyCustomSearch}
      />

      {/* Condition builder over the server-owned template filter registry. Applies
          alongside (AND) any custom search — not instead of it. */}
      <TemplateCustomFilterDialog
        open={filterOpen}
        initialBlob={customFilter}
        showIncludeDeleted
        initialIncludeDeleted={filterIncludeDeleted}
        onClose={() => setFilterOpen(false)}
        onApply={applyCustomFilter}
      />

      {/* New Template is the SHARED create flow (CreateOrderFlowProvider, mounted
          once in DashboardLayout) in template mode — same modal, same drawer, same
          staging as a new order, and nothing is persisted until Save. */}

      {/* Pro detail slide-over — clicking a template row opens it here (in place),
          the same drawer used across the app. Templates ("T110") ride it too. */}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null && !changeAllTemplates}
        onClose={handleDrawerClose}
      />

      {/* Change All — the same drawer, anchored on the first target template, with the
          "apply to all" checkboxes live. Flagged fields fan out on Save. */}
      <ProDetailSlideOver
        pronumber={changeAllTemplates?.[0] ?? null}
        isOpen={!!changeAllTemplates}
        onClose={closeChangeAll}
        changeAll={changeAllTemplates ? { targetPros: changeAllTemplates } : null}
      />
    </div>
  );
};

/**
 * Provider wrapper: ViewedProsContext holds the "which template IDs have I
 * opened" set (in-memory, session-only) that drives the green pill + last-opened
 * ring on the Template # column. It must sit ABOVE the grid so the highlight
 * survives the Template ↔ Line Items toggle, which remounts TanTable.
 */
const TemplatesPage: React.FC = () => (
  <ViewedProsProvider>
    <TemplatesPageInner />
  </ViewedProsProvider>
);

export default TemplatesPage;
